import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentsIcon from '@mui/icons-material/Payments';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAppSelector } from '../store';
import { BRAND, initialsOf } from '../theme';

interface AppHeaderProps {
  onSignOut: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const FreightWarriorsMark: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
      <path
        d="M16 2 3 6.5v9.2c0 7 5.4 12.4 13 14.3 7.6-1.9 13-7.3 13-14.3V6.5L16 2Z"
        fill="#fff"
        fillOpacity="0.12"
        stroke="#fff"
        strokeWidth="1.6"
      />
      <path d="M16 8.5 9.5 13l6.5 3 6.5-3L16 8.5Z" fill="#fff" />
      <path d="M9.5 16.5 16 20l6.5-3.5V20L16 23.5 9.5 20v-3.5Z" fill="#fff" fillOpacity="0.75" />
    </svg>
    <Box sx={{ lineHeight: 1 }}>
      <Typography
        component="span"
        sx={{ display: 'block', fontWeight: 800, fontSize: 14, letterSpacing: 0.6 }}
      >
        FREIGHT
      </Typography>
      <Typography
        component="span"
        sx={{ display: 'block', fontWeight: 800, fontSize: 14, letterSpacing: 0.6 }}
      >
        WARRIORS
      </Typography>
    </Box>
  </Box>
);

const AppHeader: React.FC<AppHeaderProps> = ({ onSignOut }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const { user } = useAppSelector((state) => state.auth);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');

  const accountType = user?.accountType;

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];

    if (accountType === 'driver' || accountType === 'owner') {
      items.push({ label: 'Driver Portal', path: '/driver-portal', icon: <DashboardIcon fontSize="small" /> });
    }

    items.push({ label: 'Pending Loads', path: '/pending-loads', icon: <LocalShippingIcon fontSize="small" /> });
    items.push({ label: 'Payments', path: '/payments', icon: <PaymentsIcon fontSize="small" /> });

    if (accountType === 'admin' || accountType === 'owner') {
      items.push({ label: 'Load Board', path: '/load-board', icon: <DashboardIcon fontSize="small" /> });
    }

    if (accountType === 'admin') {
      items.push({ label: 'Data Entry', path: '/data-entry', icon: <DescriptionIcon fontSize="small" /> });
      items.push({ label: 'Drivers', path: '/admin/users', icon: <PeopleIcon fontSize="small" /> });
    }

    items.push({ label: 'Reports', path: '/reports', icon: <InsightsIcon fontSize="small" /> });
    items.push({ label: 'Settings', path: '/settings', icon: <SettingsIcon fontSize="small" /> });

    return items;
  }, [accountType]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  const go = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  // The header search is a shortcut into the invoice list; the Pending Loads
  // page picks the term up from the query string.
  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const term = search.trim();
    navigate(term ? `/pending-loads?q=${encodeURIComponent(term)}` : '/pending-loads');
  };

  const displayName = user?.displayName || user?.email || 'Account';

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: `linear-gradient(90deg, ${BRAND.navy} 0%, ${BRAND.navyLight} 100%)`,
          color: '#fff'
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: { xs: 60, md: 64 }, px: { xs: 1.5, md: 2.5 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
          >
            <MenuIcon />
          </IconButton>

          <Box
            onClick={() => go('/pending-loads')}
            sx={{ cursor: 'pointer', display: { xs: 'none', sm: 'block' } }}
          >
            <FreightWarriorsMark />
          </Box>

          {/* Tab strip — hidden on smaller screens, where the drawer takes over */}
          <Box sx={{ display: isCompact ? 'none' : 'flex', alignItems: 'stretch', ml: 1, flex: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => go(item.path)}
                startIcon={item.icon}
                disableRipple
                sx={{
                  color: '#fff',
                  px: 1.75,
                  borderRadius: 0,
                  height: 64,
                  fontSize: 12.5,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  opacity: isActive(item.path) ? 1 : 0.82,
                  backgroundColor: isActive(item.path) ? 'rgba(255,255,255,0.16)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {isCompact && <Box sx={{ flex: 1 }} />}

          <Box
            component="form"
            onSubmit={submitSearch}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              height: 38,
              minWidth: 220,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.16)'
            }}
          >
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search anything..."
              sx={{ color: '#fff', fontSize: 14, flex: 1, '& input::placeholder': { opacity: 0.8 } }}
            />
            <IconButton type="submit" size="small" sx={{ color: '#fff' }} aria-label="Search">
              <SearchIcon fontSize="small" />
            </IconButton>
          </Box>

          <Tooltip title="Notifications">
            <IconButton color="inherit" onClick={(e) => setNotificationsAnchor(e.currentTarget)}>
              <Badge color="error" variant="dot" overlap="circular">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Button
            onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ color: '#fff', textTransform: 'none', pl: 0.5 }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                mr: 1,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.25)'
              }}
            >
              {initialsOf(displayName)}
            </Avatar>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {displayName}
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={() => setNotificationsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 300 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2">Notifications</Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You&apos;re all caught up.
          </Typography>
        </Box>
      </Menu>

      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={() => setAccountMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{displayName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {accountType ? accountType.toUpperCase() : ''}
            {user?.dogtag ? ` · ${user.dogtag}` : ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAccountMenuAnchor(null);
            go('/settings');
          }}
        >
          <SettingsIcon fontSize="small" style={{ marginRight: 8 }} />
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAccountMenuAnchor(null);
            onSignOut();
          }}
        >
          <LogoutIcon fontSize="small" style={{ marginRight: 8 }} />
          Sign out
        </MenuItem>
      </Menu>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 264 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              background: BRAND.navy,
              color: '#fff'
            }}
          >
            <FreightWarriorsMark />
            <IconButton color="inherit" onClick={() => setDrawerOpen(false)} aria-label="Close navigation">
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {navItems.map((item) => (
              <ListItemButton
                key={item.path}
                selected={isActive(item.path)}
                onClick={() => go(item.path)}
              >
                <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>{item.icon}</Box>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            <Divider sx={{ my: 1 }} />
            <ListItemButton onClick={onSignOut}>
              <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>
                <LogoutIcon fontSize="small" />
              </Box>
              <ListItemText primary="Sign out" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default AppHeader;
